import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function AuthPage({ mode = "login" }) {
  const nav = useNavigate();
  const { login, signup } = useAuth();
  const isSignup = mode === "signup";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSignup) await signup(email, password, name);
      else await login(email, password);
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogleAuth = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 relative overflow-hidden bg-surface">
      {/* Organic blurred shapes */}
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-primary-container/60 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full bg-tertiary-container/60 blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 w-[300px] h-[300px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-secondary-container/40 blur-[100px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-hero bg-primary text-primary-foreground flex items-center justify-center font-medium text-lg">Æ</div>
            <span className="text-2xl font-medium text-surface-on tracking-tight">Aevum</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-medium text-surface-on tracking-tight leading-tight">
            {isSignup ? "Your admin, in one place" : "Welcome back"}
          </h1>
          <p className="text-surface-onVariant mt-2 text-base">
            {isSignup ? "The personal operating system for everything you have to keep on top of." : "Sign in to your Aevum."}
          </p>
        </div>

        <form onSubmit={submit} className="bg-surface-container rounded-hero p-8 space-y-5 shadow-sm">
          {/* Google sign-in */}
          <button
            type="button"
            data-testid="google-signin-btn"
            onClick={startGoogleAuth}
            className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-surface border border-outline-variant text-surface-on font-medium py-3 px-6 active:scale-[0.98] hover:bg-primary/8 transition-[transform,background-color] duration-200 ease-md"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.9 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 34.9 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.6 39.5 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2C41.9 34.9 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-outline-variant" />
            <span className="text-xs text-surface-onVariant uppercase tracking-widest">or</span>
            <div className="flex-1 h-px bg-outline-variant" />
          </div>

          {isSignup && (
            <div>
              <label className="text-xs font-medium tracking-widest uppercase text-surface-onVariant">Name</label>
              <input
                data-testid="signup-name-input"
                type="text" required value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full bg-surface-containerHigh rounded-t-lg rounded-b-none border-b-2 border-outline focus:border-primary outline-none px-4 py-3 text-surface-on transition-colors duration-200 ease-md"
                placeholder="Alex Kim"
              />
            </div>
          )}
          <div>
            <label className="text-xs font-medium tracking-widest uppercase text-surface-onVariant">Email</label>
            <input
              data-testid="auth-email-input"
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full bg-surface-containerHigh rounded-t-lg rounded-b-none border-b-2 border-outline focus:border-primary outline-none px-4 py-3 text-surface-on transition-colors duration-200 ease-md"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="text-xs font-medium tracking-widest uppercase text-surface-onVariant">Password</label>
            <input
              data-testid="auth-password-input"
              type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full bg-surface-containerHigh rounded-t-lg rounded-b-none border-b-2 border-outline focus:border-primary outline-none px-4 py-3 text-surface-on transition-colors duration-200 ease-md"
              placeholder="At least 6 characters"
            />
          </div>
          <button
            data-testid="auth-submit-button"
            type="submit" disabled={loading}
            className="w-full rounded-full bg-primary text-primary-foreground font-medium py-3.5 px-6 active:scale-[0.98] hover:brightness-110 transition-[transform,filter] duration-200 ease-md disabled:opacity-60"
          >
            {loading ? "Please wait…" : isSignup ? "Create your Aevum" : "Sign in"}
          </button>
          <p className="text-center text-sm text-surface-onVariant">
            {isSignup ? "Already have an account? " : "New here? "}
            <Link data-testid="auth-toggle-link" to={isSignup ? "/login" : "/signup"} className="text-primary font-medium hover:underline">
              {isSignup ? "Sign in" : "Create an account"}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
